/**
 * pvp_context.ts — the client's read of the ground PvP sessions (2026-09-18):
 * my live duel, my live match and its members, my enrollment and its
 * sector, and the LEGAL player targets those imply. A mirror of the
 * server's pvp_policy for the purpose of never OFFERING an illegal mark —
 * the server still decides (a refusal costs nothing and names its reason);
 * sanctuary and recovery protection are its alone.
 */
import { atlas } from '../state.js';

interface IdLike { toHexString(): string; isEqual(o: unknown): boolean }
interface TsLike { microsSinceUnixEpoch: bigint }
export interface DuelRow {
  duelId: bigint; a: IdLike; b: IdLike; state: string; ringX: number; ringZ: number; ringR: number; readyA: boolean; readyB: boolean;
  outSinceA: TsLike; outSinceB: TsLike; gen: number; invitedAt: TsLike; startsAt: TsLike; endsAt: TsLike; winner: IdLike; result: string; finishedAt: TsLike;
}
export interface MatchRow {
  matchId: bigint; state: string; teamSize: number; round: number; winsA: number; winsB: number; draws: number;
  relayX: number; relayZ: number; relayR: number; stagingAx: number; stagingAz: number; stagingBx: number; stagingBz: number;
  capA: number; capB: number; roundEndsAt: TsLike; relayActiveAt: TsLike; roundStartedAt: TsLike; readyDeadline: TsLike; winner: string; result: string; finishedAt: TsLike;
}
export interface MemberRow { memberId: bigint; matchId: bigint; identity: IdLike; team: string; ready: boolean; alive: boolean; absent: boolean; active: boolean }
export interface EnrollRow { identity: IdLike; sectorId: bigint; side: string; state: string; stateAt: TsLike; lockUntil: TsLike; leaveAt: TsLike }
export interface SectorRow { sectorId: bigint; name: string; x: number; z: number; r: number; relayR: number; sideA: string; sideB: string; active: boolean; capA: number; capB: number; scoreA: number; scoreB: number }
export interface ActorRow { identity: IdLike; life: number; attackReadyAt: TsLike; equipReadyAt: TsLike; pendingActionId: bigint; combatLockUntil: TsLike; protectedUntil: TsLike }
export interface ResultRow { resultId: bigint; actionId: bigint; attacker: IdLike; targetKind: string; targetPlayer: IdLike; targetId: bigint; outcome: string; dmg: number; itemKey: string; contextKind: string; at: TsLike }
export interface ActionRow { scheduledId: bigint; attacker: IdLike; targetKind: string; targetPlayer: IdLike; itemKey: string; startedAt: TsLike; impactAt: TsLike; contextKind: string }
export interface QueueRow { identity: IdLike; teamSize: number; siteId: bigint; joinedAt: TsLike }
export interface RecordRow { identity: IdLike; duelsWon: number; duelsLost: number; duelsDrawn: number; matchesWon: number; matchesLost: number; matchesDrawn: number; objectives: number }

type Db = Record<string, { iter(): Iterable<unknown> } | undefined>;
export function rows<T>(name: string): T[] {
  try {
    const t = (atlas.conn?.db as unknown as Db)[name];
    return t ? ([...t.iter()] as T[]) : [];
  } catch { return []; }
}
const meHex = (): string | null => atlas.identityHex;
const isMe = (id: IdLike): boolean => { const m = meHex(); return m !== null && id.toHexString() === m; };
export const usOf = (t: TsLike): number => Number(t.microsSinceUnixEpoch / 1000n);
export const nowMs = (): number => Date.now();

export function myDuel(): DuelRow | null {
  let latestFinished: DuelRow | null = null;
  for (const d of rows<DuelRow>('pvpDuel')) {
    if (!isMe(d.a) && !isMe(d.b)) continue;
    if (d.state !== 'finished') return d;
    if (!latestFinished || d.duelId > latestFinished.duelId) latestFinished = d;
  }
  return latestFinished;
}
export function pendingInvite(): DuelRow | null {
  for (const d of rows<DuelRow>('pvpDuel')) if (d.state === 'invited' && isMe(d.b)) return d;
  return null;
}
export function myMember(): MemberRow | null {
  for (const m of rows<MemberRow>('pvpArenaMember')) if (m.active && isMe(m.identity)) return m;
  return null;
}
export function matchOf(matchId: bigint): MatchRow | null {
  for (const m of rows<MatchRow>('pvpArenaMatch')) if (m.matchId === matchId) return m;
  return null;
}
export function membersOf(matchId: bigint): MemberRow[] {
  return rows<MemberRow>('pvpArenaMember').filter((m) => m.matchId === matchId);
}
export function myQueue(): QueueRow | null {
  for (const q of rows<QueueRow>('pvpArenaQueue')) if (isMe(q.identity)) return q;
  return null;
}
export function myEnrollment(): EnrollRow | null {
  for (const e of rows<EnrollRow>('pvpEnrollment')) if (isMe(e.identity)) return e;
  return null;
}
export function sectorOf(sectorId: bigint): SectorRow | null {
  for (const s of rows<SectorRow>('pvpSector')) if (s.sectorId === sectorId) return s;
  return null;
}
export function nearestSector(x: number, z: number): SectorRow | null {
  let best: SectorRow | null = null, bd = Infinity;
  for (const s of rows<SectorRow>('pvpSector')) {
    if (!s.active) continue;
    const d = Math.hypot(s.x - x, s.z - z);
    if (d < bd) { bd = d; best = s; }
  }
  return best;
}
export function myActor(): ActorRow | null {
  for (const a of rows<ActorRow>('combatActor')) if (isMe(a.identity)) return a;
  return null;
}
export function myRecord(): RecordRow | null {
  for (const r of rows<RecordRow>('pvpRecord')) if (isMe(r.identity)) return r;
  return null;
}
/** The committed blow aimed at me with the nearest impact, if any. */
export function tellAtMe(): ActionRow | null {
  let best: ActionRow | null = null;
  for (const a of rows<ActionRow>('combatAction')) {
    if (a.targetKind !== 'player' || !isMe(a.targetPlayer)) continue;
    if (!best || usOf(a.impactAt) < usOf(best.impactAt)) best = a;
  }
  return best;
}

// ── BOUNTIES + GROUND WARS (2026-09-24): the two consent contexts outside a session ──────────────────────────
// The server's order is duel → arena → bounty → wardec → field (pvp_policy_core); these mirror its facts.
interface BountyRowC { bountyId: bigint; target: IdLike; state: string; expiresAt: TsLike }
interface TakeRowC { bountyId: bigint; hunter: IdLike; state: string }
export interface GroundWarRow {
  warId: bigint; attackerKind: string; attackerId: bigint; attackerName: string; defenderKind: string; defenderId: bigint; defenderName: string;
  declaredBy: IdLike; state: string; stakeA: bigint; stakeB: bigint; scoreA: number; scoreB: number; result: string;
  peaceA: boolean; peaceB: boolean; lastBannerA: TsLike; lastBannerB: TsLike; declaredAt: TsLike; activeAt: TsLike; endsAt: TsLike; endedAt: TsLike;
}

/** Player hexes a live bounty ties me to: the marks I hunt, and the hunters hunting me (either may strike). */
export function bountyTargets(): Set<string> {
  const out = new Set<string>();
  const me = meHex();
  if (!me) return out;
  const now = nowMs();
  const bounties = new Map<string, BountyRowC>();
  for (const b of rows<BountyRowC>('playerBounty')) if (b.state === 'open' && usOf(b.expiresAt) > now) bounties.set(b.bountyId.toString(), b);
  for (const t of rows<TakeRowC>('playerBountyHunter')) {
    if (t.state !== 'hunting') continue;
    const b = bounties.get(t.bountyId.toString());
    if (!b) continue;
    if (isMe(t.hunter)) out.add(b.target.toHexString());
    else if (isMe(b.target)) out.add(t.hunter.toHexString());
  }
  out.delete(me);
  return out;
}

/** Is `hex` a member of a war's side? The mirror of ground_war_ops.isMemberOf over the replicated rows. */
export function memberOfEntity(kind: string, id: bigint, hex: string): boolean {
  switch (kind) {
    case 'city': {
      for (const c of rows<{ cityId: bigint; mayor: IdLike }>('city')) if (c.cityId === id && c.mayor.toHexString() === hex) return true;
      for (const z of rows<{ cityId: bigint; identity: IdLike }>('citizen')) if (z.cityId === id && z.identity.toHexString() === hex) return true;
      return false;
    }
    case 'civ': {
      const cities = new Set<string>();
      for (const z of rows<{ cityId: bigint; identity: IdLike }>('citizen')) if (z.identity.toHexString() === hex) cities.add(z.cityId.toString());
      for (const c of rows<{ cityId: bigint; mayor: IdLike }>('city')) if (c.mayor.toHexString() === hex) cities.add(c.cityId.toString());
      for (const m of rows<{ cityId: bigint; civId: bigint }>('playerCivMember')) if (m.civId === id && cities.has(m.cityId.toString())) return true;
      return false;
    }
    case 'guild':
      for (const m of rows<{ guildId: bigint; identity: IdLike }>('guildMember')) if (m.guildId === id && m.identity.toHexString() === hex) return true;
      return false;
    case 'group':
      for (const m of rows<{ groupId: bigint; identity: IdLike }>('groupMember')) if (m.groupId === id && m.identity.toHexString() === hex) return true;
      return false;
  }
  return false;
}

/** My side in a war ('a' attacker, 'b' defender), or null — both or neither is no side (sideIn's rule). */
export function sideInWar(w: GroundWarRow, hex: string): 'a' | 'b' | null {
  const a = memberOfEntity(w.attackerKind, w.attackerId, hex);
  const b = memberOfEntity(w.defenderKind, w.defenderId, hex);
  if (a === b) return null;
  return a ? 'a' : 'b';
}

/** The active ground wars I fight in, with my side. */
export function myWars(): Array<{ war: GroundWarRow; side: 'a' | 'b' }> {
  const me = meHex();
  if (!me) return [];
  const now = nowMs();
  const out: Array<{ war: GroundWarRow; side: 'a' | 'b' }> = [];
  for (const w of rows<GroundWarRow>('groundWar')) {
    if (w.state !== 'active' || usOf(w.endsAt) <= now) continue;
    const side = sideInWar(w, me);
    if (side) out.push({ war: w, side });
  }
  return out;
}

/** Player hexes on the OTHER side of an active war I fight in (those I see as remotes are all that matter). */
export function warTargets(candidates?: Iterable<string>): Set<string> {
  const out = new Set<string>();
  const wars = myWars();
  if (wars.length === 0) return out;
  const pool = candidates ? [...candidates] : [...atlas.remotes.keys()];
  for (const hex of pool) {
    for (const { war, side } of wars) {
      const s = sideInWar(war, hex);
      if (s && s !== side) { out.add(hex); break; }
    }
  }
  return out;
}

/** The context I am fighting in, for the HUD and the hit-stop dose. */
export type PvpContextKind = 'duel' | 'arena' | 'bounty' | 'wardec' | 'field' | null;
export function myContext(): PvpContextKind {
  const d = myDuel();
  if (d && d.state === 'active') return 'duel';
  const mem = myMember();
  if (mem) { const m = matchOf(mem.matchId); if (m && m.state === 'round') return 'arena'; }
  if (bountyTargets().size > 0) return 'bounty';
  if (myWars().length > 0) return 'wardec';
  const e = myEnrollment();
  if (e && e.state !== 'enrolling') return 'field';
  return null;
}

/** The player hexes I may lawfully strike RIGHT NOW (the policy's mirror):
 *  my duel's other member while it is active; the opposing LIVE members of
 *  my match while its round runs; a bounty's mark or its hunter; the other
 *  side of a ground war I fight in; the opposite side's eligible/engaged in my
 *  sector. Anything else is never offered as a mark. */
export function legalPlayerTargets(): Set<string> {
  const out = new Set<string>();
  const me = meHex();
  if (!me) return out;
  const d = myDuel();
  if (d && d.state !== 'finished') {
    if (d.state === 'active') out.add(isMe(d.a) ? d.b.toHexString() : d.a.toHexString());
    return out; // inside a duel nothing else is legal
  }
  const mem = myMember();
  if (mem) {
    const m = matchOf(mem.matchId);
    if (m && m.state === 'round' && mem.alive) {
      for (const o of membersOf(mem.matchId)) if (o.team !== mem.team && o.alive) out.add(o.identity.toHexString());
    }
    return out;
  }
  // A bounty (a hunter and its mark) and a declared ground war (the other side's members) are lawful anywhere
  // in the Commons — the server's own flags and sanctuaries still decide each blow.
  for (const h of bountyTargets()) out.add(h);
  for (const h of warTargets()) out.add(h);
  const e = myEnrollment();
  if (e && (e.state === 'eligible' || e.state === 'engaged' || e.state === 'leaving')) {
    const s = sectorOf(e.sectorId);
    if (s && s.active) {
      for (const o of rows<EnrollRow>('pvpEnrollment')) {
        if (o.sectorId !== e.sectorId || o.side === e.side || o.state === 'enrolling' || isMe(o.identity)) continue;
        out.add(o.identity.toHexString());
      }
    }
  }
  return out;
}

/** A player's display name from the account table (falls back to a short hex). */
export function playerName(hex: string): string {
  for (const a of rows<{ identity: IdLike; username: string; displayName: string }>('account')) {
    if (a.identity.toHexString() === hex) return a.displayName || a.username || hex.slice(0, 8);
  }
  return hex.slice(0, 8);
}
export function identityByName(name: string): string | null {
  const n = name.toLowerCase();
  for (const a of rows<{ identity: IdLike; username: string; displayName: string }>('account')) {
    if (a.username.toLowerCase() === n || a.displayName.toLowerCase() === n) return a.identity.toHexString();
  }
  return null;
}
